import { ChainInfo } from "@gm-network/chains";

export type { ChainInfo };

export type Account = {
  isDeployed: boolean;
  chainId: number;
  eoaAddress: string;
  factoryAddress: string;
  entryPointAddress: string;
  smartAccountAddress: string;
  owner: string;
  index: number;
  implementationAddress: string;
  implementationVersion: string;
  fallBackHandlerAddress: string;
  version: string;
} & any; // particle_aa.Account

export type AACore = {
  getChainId(): Promise<string>;
  getAccount(): Promise<Account>;
  getAddress(): Promise<string>;
  getOwner(): Promise<string>;
  isDeployed(): Promise<boolean>;
  deployWalletContract(): Promise<string>;
  sendRpc<T>(arg: { method: string; params?: any[] }): Promise<T>;
  getFeeQuotes(tx: Transaction | Transaction[]): Promise<FeeQuotes>;
  buildUserOperation({
    tx,
    feeQuote,
    tokenPaymasterAddress,
  }: UserOpParams): Promise<UserOpBundle>;
  signUserOperation({
    userOpHash,
    userOp,
  }: UserOpBundle): Promise<UserOpBundle["userOp"]>;
  sendUserOperation({ userOpHash, userOp }: UserOpBundle): Promise<string>;
  sendSignedUserOperation(
    userOp: UserOpBundle["userOp"],
    sessionDataParams?: any
  ): Promise<string>;
  sendTransaction(params: UserOpBundle | UserOpParams): Promise<string>;
  createSessions(options: any[]): Promise<FeeQuotes>;
  validateSession(targetSession: any, sessions: any[]): Promise<boolean>;
} & any; // particle_aa.SmartAccount

export type AAProvider = {
  resolveSendTransaction(
    params: UserOpBundle | Omit<UserOpParams, "tx"> // particle_aa.ResolveTransactionParams
  ): void;
  rejectSendTransaction(error: Error): void;
  on(event: string, listener: any): void;
  once(event: string, listener: any): void;
  off(event: string, listener: any): void;
  removeListener(event: string, listener: any): void;
  enable(): Promise<string[]>;
  request(payload: Partial<JsonRpcRequest>): Promise<any>;
} & any; // particle_aa.AAWrapProvider

export type JsonRpcRequest = {
  id: number | string;
  jsonrpc: string;
  method: string;
  params?: any[];
} & any; // particle_aa.JsonRpcRequest

export type Transaction = {
  to: string;
  value?: string;
  data?: string;
  nonce?: number | string;
  gasLimit?: number | string;
} & any; // particle_aa.Transaction

export type UserOpBundle = {
  userOp: {
    sender: string;
    nonce: string;
    initCode: string;
    callData: string;
    signature: string;
    maxFeePerGas: string;
    maxPriorityFeePerGas: string;
    verificationGasLimit: string;
    callGasLimit: string;
    preVerificationGas: string;
    paymasterAndData: string;
    [key: string]: any;
  };
  userOpHash: string;
} & any; // particle_aa.UserOpBundle;

export type UserOpParams = {
  tx: Transaction | Transaction[];
  feeQuote?: FeeQuote;
  tokenPaymasterAddress?: string;
} & any; // particle_aa.UserOpParams

export type FeeQuote = {
  tokenInfo: {
    chainId: number;
    address: string;
    name: string;
    symbol: string;
    decimals: number;
    logoURI: string;
  };
  fee: string;
  balance: string;
  premiumPercentage?: string;
} & any; // particle_aa.FeeQuote

export type FeeQuotes = {
  verifyingPaymasterGasless?: UserOpBundle & { feeQuote?: FeeQuote };
  verifyingPaymasterNative: UserOpBundle & { feeQuote?: FeeQuote };
  tokenPaymaster?: {
    tokenPaymasterAddress: string;
    feeQuotes: FeeQuote[];
  };
  sessions?: {
    validUntil: number;
    validAfter: number;
    sessionValidationModule: string;
    sessionKeyData: string;
  }[];
  transactions?: unknown[];
} & any; // particle_aa.FeeQuotesResponse

// ------ @gm-network/particle ------
export const authCoreEvents = {
  connect: "particleAuthConnect", // connect auth core success event, emit before restore wallet
  disconnect: "particleAuthDisconnect", // disconnect event
  userInfoChanged: "userInfoChanged", // disconnect event

  requestEthereumAccount: "requestEthereumAccount", // request connect event
  requestSolanaAccount: "requestSolanaAccount", // request connect event
  masterPasswordChanged: "masterPasswordChanged", // master password changed event
  walletRestored: "particleWalletRestored",
} as const;
type AuthCoreEvents = typeof authCoreEvents;
export type AuthCoreEvent = AuthCoreEvents[keyof AuthCoreEvents];

export type PrefixedHexString = string;

export type ConnectParam = (
  | {
      phone: string;
      code?: string;
    }
  | {
      email?: string;
      code?: string;
    }
  | {
      provider: "jwt" | "telegram";
      thirdpartyCode: string;
    }
  | {
      socialType:
        | "facebook"
        | "google"
        | "apple"
        | "twitter"
        | "discord"
        | "github"
        | "twitch"
        | "microsoft"
        | "linkedin";
      code?: string;
      nonce?: string;
      prompt?: "none" | "consent" | "select_account";
    }
) & {
  chainId?: number;
  authorization?:
    | {
        uniq: boolean;
      }
    | { uniq: boolean; message: string };
};

export interface ParticleAuth {
  // useEvents
  events: {
    // once(event: string, listener: (...args: any[]) => void): void;
    // off(event: string, listener: (...args: any[]) => void): void;
    on(event: AuthCoreEvent | string, listener: (...args: any[]) => void): void;
    removeListener(
      event: AuthCoreEvent | string,
      listener: (...args: any[]) => void
    ): void;
  };
  // useConnect
  connect(options?: ConnectParam): Promise<any>;
  disconnect(): Promise<void>;
  requestConnectCaptcha(
    config:
      | {
          email: string;
        }
      | {
          phone: string;
          cloudflareOptions?: {
            getContainer?: HTMLElement | (() => HTMLElement) | false;
            theme?: "light" | "dark";
            language?: string;
          };
        }
  ): Promise<boolean>;
  setSocialConnectCallback(callback?: {
    onSuccess?: (
      info: {
        uuid: string;
        created_at: string;
        updated_at: string;
        mac_key: string;
        token: string;
        wallets: any[];
        cognito_result: any;
        security_account: any;
        name?: string;
      } & any
    ) => void;
    onError?: (
      error:
        | {
            error_code: number;
            message: string;
          }
        | Error
    ) => void;
  }): void;
}

export interface ParticleEthereum {
  // useEthereum
  provider: ParticleProvider;
  enable(): Promise<string>;
  switchChain(id: number | PrefixedHexString): Promise<void>;
  signMessage(
    message: PrefixedHexString | string,
    uniq?: boolean
  ): Promise<string>;
  sendTransaction(transaction: any): Promise<string>;
}

export interface ParticleProvider {
  on(
    event:
      | "connect"
      | "disconnect"
      | "message"
      | "chainChanged"
      | "accountsChanged"
      | string,
    listener: (...args: any[]) => void
  ): void;
  once(event: string, listener: (...args: any[]) => void): void;
  off(event: string, listener: (...args: any[]) => void): void;
  removeListener(event: string, listener: (...args: any[]) => void): void;
  request(data: {
    id: string;
    jsonrpc: string;
    method: string;
    params?: unknown[];
    chainId?: number;
  }): Promise<any>;

  get selectedAddress(): string | null;
  get chainId(): string;
  isConnected(): boolean;
  isParticleNetwork: boolean;
  signMessage(
    message: PrefixedHexString | string,
    uniq?: boolean
  ): Promise<string>;
  sendTransaction(transaction: any): Promise<string>;

  /**
   * Deprecated: user request instead
   */
  enable(): Promise<string[]>;
  connect(options?: ConnectParam): Promise<string[]>;
  disconnect(): Promise<void>;
}

export interface ParticleStatus {
  connected: boolean;
  connectionStatus: "loading" | "connecting" | "connected" | "disconnected";
  address: string | null;
  chainId: number;
  chainInfo: any;

  on(
    event: "connect" | "disconnect" | string,
    listener: (...args: any[]) => void
  ): void;
  removeListener(
    event: "connect" | "disconnect" | string,
    listener: (...args: any[]) => void
  ): void;
}

export interface WindowWithParticle {
  particle?: {
    auth?: ParticleAuth;
    wallet?: ParticleEthereum;
    status?: ParticleStatus;
  };
}
